import { useCallback, useEffect, useState } from 'react';

/**
 * React-side bridge to window.__consent (client/public/consent.js). Keeps
 * every component from touching `window` directly, mirroring how
 * Footer.js already guarded `window.Termly` before this replaced it.
 */
const getSnapshot = () => (typeof window !== 'undefined' && window.__consent ? window.__consent.get() : null);

export const useConsent = () => {
  const [state, setState] = useState(getSnapshot);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.__consent) return undefined;
    // Pick up any decision made between initial render and this effect
    // running, then stay subscribed for future changes.
    setState(window.__consent.get());
    return window.__consent.subscribe(setState);
  }, []);

  const setConsent = useCallback((next) => {
    if (typeof window !== 'undefined' && window.__consent) {
      window.__consent.set(next);
    }
  }, []);

  return { consent: state, hasDecision: !!state, setConsent };
};

/** Registers a callback for window.__consent.openPreferences() calls (the
 * footer's "Consent Preferences" button, and any other trigger). */
export const useConsentPreferencesOpener = (onOpen) => {
  useEffect(() => {
    if (typeof window === 'undefined' || !window.__consent) return undefined;
    return window.__consent.onOpenPreferences(onOpen);
  }, [onOpen]);
};

/** Imperative call for places outside React's tree, or that don't want the
 * hook (e.g. a plain onClick handler). */
export const openConsentPreferences = () => {
  if (typeof window !== 'undefined' && window.__consent && typeof window.__consent.openPreferences === 'function') {
    window.__consent.openPreferences();
  }
};
